import React, { useMemo } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Input,
  Tag,
  Alert,
  Spin,
  message,
  Segmented,
  Select,
  Table,
  Descriptions,
} from "antd";
import {
  ContainerOutlined,
  SearchOutlined,
  LinkOutlined,
  LockOutlined,
  CopyOutlined,
  UnorderedListOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { validStatuses } from "../../../utils/bookingStatus";
import {
  buildBotBookingPayload,
  formatBotBookingJson,
  formatBookingEffLabel,
  type BookingInsight,
  type BookingListRow,
  type BotInsightsMode,
  type LookupResult,
} from "./botAdminShared";
import { summarizeBotBookingPayload } from "./botPayloadSummary";

const { Text, Paragraph } = Typography;

interface BotInsightsStepProps {
  selectedResult: LookupResult | null;
  selectedCompanyId: number | null;
  mode: BotInsightsMode;
  onModeChange: (mode: BotInsightsMode) => void;
  bookingQuery: string;
  onBookingQueryChange: (value: string) => void;
  statusFilter: string | null;
  onStatusFilterChange: (status: string | null) => void;
  bookingLoading: boolean;
  listLoading: boolean;
  bookingSearched: boolean;
  bookingInsight: BookingInsight | null;
  bookingList: BookingListRow[];
  onLookup: () => void;
  onLoadRecent: () => void;
  onLoadAll: () => void;
  onLoadByStatus: () => void;
  onSelectListBooking: (bookingId: number) => void;
  onBack: () => void;
  onContinue: () => void;
}

export const BotInsightsStep: React.FC<BotInsightsStepProps> = ({
  selectedResult,
  selectedCompanyId,
  mode,
  onModeChange,
  bookingQuery,
  onBookingQueryChange,
  statusFilter,
  onStatusFilterChange,
  bookingLoading,
  listLoading,
  bookingSearched,
  bookingInsight,
  bookingList,
  onLookup,
  onLoadRecent,
  onLoadAll,
  onLoadByStatus,
  onSelectListBooking,
  onBack,
  onContinue,
}) => {
  const botPayload = useMemo(() => {
    if (!bookingInsight || selectedCompanyId == null) return null;
    return buildBotBookingPayload(bookingInsight, selectedCompanyId);
  }, [bookingInsight, selectedCompanyId]);

  const botJson = useMemo(
    () => (botPayload ? formatBotBookingJson(botPayload) : null),
    [botPayload],
  );

  const botSummary = useMemo(
    () => (botPayload ? summarizeBotBookingPayload(botPayload) : []),
    [botPayload],
  );

  const handleCopy = async () => {
    if (!botJson) return;
    try {
      await navigator.clipboard.writeText(botJson);
      message.success("Copied pretty-printed JSON");
    } catch {
      message.error("Could not copy to clipboard");
    }
  };

  const disabled = selectedCompanyId == null;
  const busy = bookingLoading || listLoading;

  return (
    <Card
      title={
        <Space>
          <ContainerOutlined />
          <span>Step 3 — Booking insights</span>
          <Tag color="blue">Lookup works in admin</Tag>
          <Tag icon={<LockOutlined />}>No public endpoint yet</Tag>
        </Space>
      }
      extra={
        <Button type="link" onClick={onBack}>
          ← Back to company
        </Button>
      }
      style={{ marginBottom: 24 }}
    >
      {selectedResult ? (
        <Alert
          type="info"
          showIcon
          message={`Scoped to ${selectedResult.company.name} (#${selectedResult.company.id})`}
          description="The bot should only return bookings that belong to this company."
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Select a company first"
          style={{ marginBottom: 16 }}
        />
      )}

      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Options for this step:
      </Paragraph>
      <Segmented
        style={{ marginBottom: 16 }}
        value={mode}
        disabled={disabled || busy}
        onChange={(value) => onModeChange(value as BotInsightsMode)}
        options={[
          {
            label: "Lookup by id / reference",
            value: "lookup",
            icon: <SearchOutlined />,
          },
          {
            label: "Recent open bookings",
            value: "recent",
            icon: <UnorderedListOutlined />,
          },
          {
            label: "All bookings",
            value: "all",
            icon: <UnorderedListOutlined />,
          },
          {
            label: "Filter by status",
            value: "status",
            icon: <FilterOutlined />,
          },
        ]}
      />

      {mode === "lookup" && (
        <>
          <Paragraph type="secondary">
            Extract a booking token from the message. Accepts{" "}
            <Text code>EFF7756SEA</Text>, <Text code>EFF7756</Text>,{" "}
            <Text code>7756</Text>.
          </Paragraph>
          <Space.Compact
            style={{ width: "100%", maxWidth: 520, marginBottom: 16 }}
          >
            <Input
              placeholder="e.g. EFF7756SEA, EFF7756, or 7756"
              value={bookingQuery}
              onChange={(e) => onBookingQueryChange(e.target.value)}
              onPressEnter={onLookup}
              disabled={disabled || busy}
              allowClear
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={bookingLoading}
              onClick={onLookup}
              disabled={disabled}
            >
              Look up
            </Button>
          </Space.Compact>
        </>
      )}

      {mode === "recent" && (
        <>
          <Paragraph type="secondary">
            List recent non-terminal bookings for this company (submitted →
            delivery planned). Click a row to load full bot JSON.
          </Paragraph>
          <Button
            type="primary"
            icon={<UnorderedListOutlined />}
            loading={listLoading}
            onClick={onLoadRecent}
            disabled={disabled}
            style={{ marginBottom: 16 }}
          >
            Load recent open bookings
          </Button>
        </>
      )}

      {mode === "all" && (
        <>
          <Paragraph type="secondary">
            List all bookings for this company (including older / terminal
            statuses), newest first. Click a row to load full bot JSON.
          </Paragraph>
          <Button
            type="primary"
            icon={<UnorderedListOutlined />}
            loading={listLoading}
            onClick={onLoadAll}
            disabled={disabled}
            style={{ marginBottom: 16 }}
          >
            Load all bookings
          </Button>
        </>
      )}

      {mode === "status" && (
        <>
          <Paragraph type="secondary">
            Filter this company’s bookings by status, then pick one for full
            insights.
          </Paragraph>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              style={{ minWidth: 260 }}
              placeholder="Select status"
              value={statusFilter}
              onChange={(value) => onStatusFilterChange(value)}
              disabled={disabled || busy}
              options={validStatuses.map((status) => ({
                value: status,
                label: status,
              }))}
              allowClear
            />
            <Button
              type="primary"
              icon={<FilterOutlined />}
              loading={listLoading}
              onClick={onLoadByStatus}
              disabled={disabled || !statusFilter}
            >
              Load by status
            </Button>
          </Space>
        </>
      )}

      {busy && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      )}

      {!busy && mode === "lookup" && bookingSearched && !bookingInsight && (
        <Alert
          type="info"
          showIcon
          message="No booking found"
          description="Nothing matched for this company. Check the id/reference, or whether the booking belongs to another company."
          style={{ marginBottom: 16 }}
        />
      )}

      {!busy &&
        (mode === "recent" || mode === "all" || mode === "status") &&
        bookingList.length > 0 && (
        <Table
          size="small"
          rowKey="id"
          style={{ marginBottom: 16 }}
          pagination={
            bookingList.length > 10 ? { pageSize: 10 } : false
          }
          dataSource={bookingList}
          onRow={(record) => ({
            onClick: () => onSelectListBooking(record.id),
            style: {
              cursor: "pointer",
              background:
                bookingInsight?.id === record.id ? "#e6f7ff" : undefined,
            },
          })}
          columns={[
            {
              title: "Reference",
              key: "ref",
              render: (_: unknown, row: BookingListRow) =>
                formatBookingEffLabel(row),
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (value: string | null) =>
                value ? <Tag>{value}</Tag> : "—",
            },
            {
              title: "ETA",
              key: "eta",
              render: (_: unknown, row: BookingListRow) =>
                row.eta ||
                row.container?.eta_customer ||
                row.container?.eta ||
                "—",
            },
            {
              title: "Container",
              key: "container",
              render: (_: unknown, row: BookingListRow) =>
                row.container?.reference || "—",
            },
            {
              title: "Created",
              dataIndex: "created_at",
              key: "created_at",
              render: (value: string | null) =>
                value ? value.slice(0, 10) : "—",
            },
          ]}
        />
      )}

      {!busy &&
        (mode === "recent" || mode === "all" || mode === "status") &&
        bookingSearched &&
        bookingList.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="No bookings in this list"
            style={{ marginBottom: 16 }}
          />
        )}

      {!busy && bookingInsight && botJson && (
        <Card
          size="small"
          type="inner"
          title={
            <Space wrap>
              <Text strong>
                {formatBookingEffLabel({
                  id: bookingInsight.id,
                  reference: bookingInsight.reference,
                  transport_type: bookingInsight.transport_type,
                })}
              </Text>
              <Text type="secondary">#{bookingInsight.id}</Text>
              {bookingInsight.status && (
                <Tag color="processing">{String(bookingInsight.status)}</Tag>
              )}
              <Link to={`/admin/bookings?booking_id=${bookingInsight.id}`}>
                <Button size="small" icon={<LinkOutlined />}>
                  Open booking
                </Button>
              </Link>
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>
                Copy JSON
              </Button>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            What’s in this payload (summary)
          </Paragraph>
          <Descriptions
            size="small"
            bordered
            column={1}
            style={{ marginBottom: 16 }}
            items={botSummary.map((line) => ({
              key: line.label,
              label: line.label,
              children: line.value,
            }))}
          />
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Bot context payload (pretty-printed JSON)
          </Paragraph>
          <pre
            style={{
              margin: 0,
              padding: 12,
              background: "#0f172a",
              color: "#e2e8f0",
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.45,
              overflow: "auto",
              maxHeight: 560,
            }}
          >
            {botJson}
          </pre>
        </Card>
      )}

      <Button
        type="primary"
        onClick={onContinue}
        disabled={selectedCompanyId == null || !bookingInsight}
      >
        Continue to bot reply (preview) →
      </Button>
    </Card>
  );
};
